import {withI18n, WithI18nProps} from '@udemy/i18n';

import classNames from 'classnames';
import {observer, Provider} from 'mobx-react';
import React from 'react';

import {
    AGGREGATION_TYPE_PRICE,
    PAGE_TYPE_CATEGORY,
    PAGE_TYPE_SUBCATEGORY,
} from 'udemy-django-static/js/browse/lib/constants';
import {BrowseCourse as Course, BrowseCourseCard} from '@udemy/browse-course';
import {DiscoveryListContainer} from 'src/features/category/components/browse/discovery-list-container/discovery-list-container.react-component';
import {Category} from 'udemy-django-static/js/category/types/category';
import {ClickEvent, Tracker, TrackingContextProvider} from '@udemy/event-tracking';
import {isomorphic} from 'udemy-django-static/js/utils/isomorphic-rendering';
import {noop} from 'udemy-django-static/js/utils/noop';

import {CommunitySection} from './components/community-section.react-component';
import {HeroFRC} from './components/hero-frc.react-component';
import {HeroVodafone} from './components/hero-vodafone.react-component';
import {SubcategoriesSection} from './components/subcategories-section.react-component';
import {UFBSection} from './components/ufb-section.react-component';

interface CategoryConfig {
    id: number;
    title: string;
    title_cleaned: string;
    subcategories: {
        id: number;
        title: string;
        title_cleaned: string;
    }[];
}

export interface FreeCategoryProps extends WithI18nProps {
    freeCategoryConfig: {
        free: CategoryConfig;
        vodafone: CategoryConfig;
    };
    categoryUrl: string;
    pageObject: {
        id: number;
        title: string;
        title_cleaned: string;
        type: typeof PAGE_TYPE_CATEGORY | typeof PAGE_TYPE_SUBCATEGORY;
    };
    subcategories?: Category[];
}

@observer
export class FreeCategory extends React.Component<FreeCategoryProps> {
    constructor(props: FreeCategoryProps) {
        super(props);
        this.ref = React.createRef();
    }

    ref: React.RefObject<HTMLDivElement>;

    handlePageChange = (componentName?: string) => () => {
        if (componentName) {
            Tracker.publishEvent(
                new ClickEvent({
                    componentName,
                }),
            );
        }
        this.ref.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
    };

    get presetFilters() {
        return {[AGGREGATION_TYPE_PRICE]: ['price-free']};
    }

    renderStaticContent() {
        const {freeCategoryConfig, pageObject, subcategories} = this.props;

        if (pageObject.title_cleaned === freeCategoryConfig.free.title_cleaned) {
            return (
                <>
                    <div className="container--hero">
                        <div className="ud-container">
                            <HeroFRC jumpLinkHandler={this.handlePageChange('HeroFRC')} />
                        </div>
                    </div>
                    <div className="ud-container">
                        <SubcategoriesSection
                            subcategories={subcategories ?? []}
                            jumpLinkHandler={this.handlePageChange('SubcategoriesSection')}
                        />
                        <UFBSection />
                        <CommunitySection />
                    </div>
                </>
            );
        } else if (pageObject.title_cleaned === freeCategoryConfig.vodafone.title_cleaned) {
            return (
                <>
                    <div className="container--hero">
                        <div className="ud-container">
                            <HeroVodafone jumpLinkHandler={this.handlePageChange('HeroVodafone')} />
                        </div>
                    </div>
                    <div className="ud-container">
                        <div ref={this.ref}>
                            <SubcategoriesSection
                                subcategories={subcategories ?? []}
                                jumpLinkHandler={this.handlePageChange('SubcategoriesSection')}
                            />
                        </div>
                    </div>
                </>
            );
        }
    }

    renderTitle() {
        const {gettext, interpolate, freeCategoryConfig, pageObject} = this.props;

        if (
            [
                freeCategoryConfig.free.title_cleaned,
                freeCategoryConfig.vodafone.title_cleaned,
            ].includes(pageObject.title_cleaned)
        ) {
            return (
                <div ref={this.ref} data-purpose="category-header">
                    <h2 className="ud-heading-serif-xxl text-margin">
                        {gettext('Browse free courses')}
                    </h2>
                </div>
            );
        } else if (
            freeCategoryConfig.free.subcategories
                .map((sc) => sc.title_cleaned)
                .includes(pageObject.title_cleaned)
        ) {
            return (
                <div className="text-top-margin" data-purpose="subcategory-header" ref={this.ref}>
                    <h1 className="text-margin">
                        <a className="ud-heading-xxl" href={this.props.categoryUrl}>
                            {pageObject.title}
                        </a>
                    </h1>
                    <h2 className="ud-heading-xl text-margin">
                        {interpolate(
                            gettext('Free %(subcategory)s courses'),
                            {subcategory: pageObject.title},
                            true,
                        )}
                    </h2>
                </div>
            );
        }
    }

    renderCourses = (courses: Course[]) => {
        return courses.map((course, index) => (
            <TrackingContextProvider key={course.id} trackingContext={{index}}>
                <BrowseCourseCard course={course} size="large" renderPriceText={noop} />
            </TrackingContextProvider>
        ));
    };

    render() {
        const {pageObject} = this.props;
        const courseDirectoryProps = {
            hiddenFilters: ['price', 'features', 'ratings', 'duration', 'closed_captions'],
            hideNumFiltersApplied: true,
            renderCourseCard: this.renderCourses,
            hiddenSortOptions: ['price-low-to-high', 'price-high-to-low'],
        };
        const categoryWrapperStyle = classNames({
            'category-page-wrapper': pageObject.type === PAGE_TYPE_CATEGORY,
        });

        return (
            <>
                {this.renderStaticContent()}
                <div className={categoryWrapperStyle}>
                    <div className="ud-container">
                        {this.renderTitle()}
                        {typeof window !== 'undefined' && (
                            <Provider isConsumerSubsSubscriber={false}>
                                <DiscoveryListContainer
                                    pageType={pageObject.type}
                                    pageSize={16}
                                    pageObjectId={Number(pageObject?.id)}
                                    presetFilters={this.presetFilters}
                                    courseCardSize="small"
                                    onPageChange={this.handlePageChange}
                                    filterOrder={['topic', 'language']}
                                    courseDirectoryProps={courseDirectoryProps}
                                    query={this.props.pageObject.title}
                                />
                            </Provider>
                        )}
                    </div>
                </div>
            </>
        );
    }
}

export default isomorphic(withI18n(FreeCategory));
